import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/components/layout/app-shell";
import { ModulePage } from "@/features/shared/module-page";
import { Skeleton } from "@/components/ui/skeleton";
import { RequireAuth, RequirePermission } from "@/features/auth/require-auth";
import { LoginPage } from "@/features/auth/pages/login-page";
import { ChangePasswordPage } from "@/features/auth/pages/change-password-page";

const DashboardPage = lazy(() =>
  import("@/features/dashboard/pages/dashboard-page").then((module) => ({
    default: module.DashboardPage,
  })),
);
const TerritoryPage = lazy(() =>
  import("@/features/territory/pages/territory-page").then((module) => ({
    default: module.TerritoryPage,
  })),
);
const PrioritiesPage = lazy(() =>
  import("@/features/priorities/pages/priorities-page").then((module) => ({
    default: module.PrioritiesPage,
  })),
);
const ActionsPage = lazy(() =>
  import("@/features/operations/pages/actions-page").then((module) => ({
    default: module.ActionsPage,
  })),
);
const AgendaPage = lazy(() =>
  import("@/features/agenda/pages/agenda-page").then((module) => ({
    default: module.AgendaPage,
  })),
);
const CommunicationPage = lazy(() =>
  import("@/features/communication/pages/communication-page").then(
    (module) => ({ default: module.CommunicationPage }),
  ),
);
const ValidationsPage = lazy(() =>
  import("@/features/validations/pages/validations-page").then((module) => ({
    default: module.ValidationsPage,
  })),
);
const UsersPage = lazy(() =>
  import("@/features/admin/pages/users-page").then((module) => ({
    default: module.UsersPage,
  })),
);
const CatalogsPage = lazy(() =>
  import("@/features/catalogs/pages/catalogs-page").then((module) => ({
    default: module.CatalogsPage,
  })),
);
const NotificationsPage = lazy(() =>
  import("@/features/notifications/pages/notifications-page").then(
    (module) => ({ default: module.NotificationsPage }),
  ),
);
const AnalyticsPage = lazy(() =>
  import("@/features/analytics/pages/analytics-page").then((module) => ({
    default: module.AnalyticsPage,
  })),
);
const DependenciesPage = lazy(() =>
  import("@/features/dependencies/pages/dependencies-page").then((module) => ({
    default: module.DependenciesPage,
  })),
);
const MonitoringPage = lazy(() =>
  import("@/features/monitoring/pages/monitoring-page").then((module) => ({
    default: module.MonitoringPage,
  })),
);

const pending = (
  <div className="grid gap-4 p-6 md:grid-cols-2">
    <Skeleton className="h-64" />
    <Skeleton className="h-64" />
  </div>
);

export function App() {
  return (
    <Suspense fallback={pending}>
      <Routes>
        <Route path="/acceso" element={<LoginPage />} />
        <Route element={<RequireAuth />}>
          <Route path="/cambiar-contrasena" element={<ChangePasswordPage />} />
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/mapa" replace />} />
            <Route
              path="/inicio"
              element={
                <RequirePermission
                  anyOf={["indicators.read.all", "indicators.read.area"]}
                >
                  <DashboardPage />
                </RequirePermission>
              }
            />
            <Route
              path="/mapa"
              element={
                <RequirePermission anyOf={["territory.read"]}>
                  <TerritoryPage />
                </RequirePermission>
              }
            />
            <Route
              path="/prioridades"
              element={
                <RequirePermission anyOf={["territory.read"]}>
                  <PrioritiesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/necesidades"
              element={
                <RequirePermission
                  anyOf={["needs.read.all", "needs.read.area"]}
                >
                  <ActionsPage view="needs" />
                </RequirePermission>
              }
            />
            <Route
              path="/acciones"
              element={
                <RequirePermission
                  anyOf={["actions.read.all", "actions.read.area"]}
                >
                  <ActionsPage view="actions" />
                </RequirePermission>
              }
            />
            <Route
              path="/levantamientos"
              element={
                <RequirePermission
                  anyOf={["needs.read.all", "needs.read.area"]}
                >
                  <ActionsPage view="needs" />
                </RequirePermission>
              }
            />
            <Route
              path="/agenda"
              element={
                <RequirePermission
                  anyOf={["agenda.read.all", "agenda.read.area"]}
                >
                  <AgendaPage />
                </RequirePermission>
              }
            />
            <Route
              path="/comunicacion"
              element={
                <RequirePermission
                  anyOf={["communication.manage", "communication.approve"]}
                >
                  <CommunicationPage />
                </RequirePermission>
              }
            />
            <Route
              path="/indicadores"
              element={
                <RequirePermission
                  anyOf={["indicators.read.all", "indicators.read.area"]}
                >
                  <DashboardPage />
                </RequirePermission>
              }
            />
            <Route
              path="/validaciones"
              element={
                <RequirePermission
                  anyOf={["validations.read.all", "validations.read.area"]}
                >
                  <ValidationsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/catalogos"
              element={
                <RequirePermission
                  anyOf={["catalogs.read.all", "catalogs.read.area"]}
                >
                  <CatalogsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/notificaciones"
              element={
                <RequirePermission anyOf={["notifications.read"]}>
                  <NotificationsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/analitica"
              element={
                <RequirePermission
                  anyOf={["analytics.read.all", "analytics.read.area"]}
                >
                  <AnalyticsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/dependencias"
              element={
                <RequirePermission
                  anyOf={["dependencies.read.all", "dependencies.read.area"]}
                >
                  <DependenciesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/monitoreo"
              element={
                <RequirePermission anyOf={["monitoring.read"]}>
                  <MonitoringPage />
                </RequirePermission>
              }
            />
            <Route
              path="/administracion"
              element={
                <RequirePermission anyOf={["admin.manage"]}>
                  <UsersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/auditoria"
              element={
                <RequirePermission anyOf={["audit.read"]}>
                  <ModulePage
                    title="Auditoría"
                    description="Trazabilidad de cambios, aprobaciones y exportaciones."
                  />
                </RequirePermission>
              }
            />
            <Route path="*" element={<Navigate to="/mapa" replace />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
